import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import Pagination from '../../../../components/Pagination/Pagination';
import './AdminStyles.css';

const AdminAvailablePins = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, available: 0, used: 0, unassigned: 0 });
  const [acting, setActing] = useState(null);
  const [showGen, setShowGen] = useState(false);
  const [genCount, setGenCount] = useState(5);
  const [assignTo, setAssignTo] = useState('');
  const [assignToFocused, setAssignToFocused] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');
  const [activeTab, setActiveTab] = useState(''); // '' = All, 'Available', 'Used'

  const fetchData = () => {
    setLoading(true);
    let url = `/api/admin/pins/available?page=${page}`;
    if (activeTab) url += `&status=${activeTab}`;
    
    apiFetch(url)
      .then(res => res.json())
      .then(res => { 
        setData(res.pins || []); 
        setTotalPages(res.totalPages || 1);
        setStats(res.stats || { total: 0, available: 0, used: 0, unassigned: 0 });
        setLoading(false); 
      })
      .catch(() => setLoading(false));
  };

  useEffect(fetchData, [page, activeTab]);

  useEffect(() => {
    apiFetch('/api/admin/users?limit=1000')
      .then(res => res.json())
      .then(res => setUsers(res.users || []))
      .catch(() => {});
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('');
    try {
      const res = await apiFetch('/api/admin/pins/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: genCount, assignTo }),
      });
      const d = await res.json();
      setGenMsg(d.message);
      if (d.success) { fetchData(); }
    } catch {
      setGenMsg('Server error.');
    }
    setGenerating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pin?')) return;
    setActing(id);
    const res = await apiFetch(`/api/admin/pins/${id}/delete`, { method: 'DELETE' });
    const d = await res.json();
    if (d.success) fetchData();
    setActing(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / E-Pins Management</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 className="admin-title" style={{ marginBottom: 0 }}>E-Pins Management</h2>
        <button className="admin-btn admin-btn--approve" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => { setShowGen(true); setGenMsg(''); }}>+ Generate Pins</button>
      </div>

      <div className="admin-stats-bar" style={{ marginTop: 16 }}>
        <div className="admin-stat-chip blue">{stats.total} Total</div>
        <div className="admin-stat-chip green">{stats.available} Available</div>
        <div className="admin-stat-chip gray">{stats.used} Used</div>
        {stats.unassigned > 0 && (
          <div className="admin-stat-chip red">{stats.unassigned} Unassigned</div>
        )}
      </div>

      {/* Tab Buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => handleTabChange('')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === '' ? 'linear-gradient(135deg, var(--awp-accent), var(--awp-accent-hover))' : 'var(--awp-surface-2)',
            color: activeTab === '' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
            boxShadow: activeTab === '' ? 'var(--awp-shadow-sm)' : 'none',
          }}
        >
          📋 All Pins
        </button>
        <button
          onClick={() => handleTabChange('Available')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === 'Available' ? 'linear-gradient(135deg, var(--awp-success), var(--awp-success-border))' : 'var(--awp-surface-2)',
            color: activeTab === 'Available' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
            boxShadow: activeTab === 'Available' ? 'var(--awp-shadow-sm)' : 'none',
          }}
        >
          ✅ Available Pins
        </button>
        <button
          onClick={() => handleTabChange('Used')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === 'Used' ? 'linear-gradient(135deg, var(--awp-text-subtle), var(--awp-text-muted))' : 'var(--awp-surface-2)',
            color: activeTab === 'Used' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
            boxShadow: activeTab === 'Used' ? 'var(--awp-shadow-sm)' : 'none',
          }}
        >
          🔒 Used Pins
        </button>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          {activeTab === 'Available' ? 'Available E-Pins' : activeTab === 'Used' ? 'Used E-Pins' : 'All E-Pins'}
        </div>
        <div className="admin-card-body">
          {loading ? (
            <Preloader message="Fetching E-Pins..." fullScreen={false} />
          ) : data.length === 0 ? (
            <div className="admin-empty">
              {activeTab === 'Available' ? 'No available pins' : activeTab === 'Used' ? 'No used pins' : 'No pins found'}
            </div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>ID</th><th>Pin Code</th><th>Status</th><th>Assigned To</th><th>Used By</th><th>TRX ID</th><th>Created</th><th>Expires At</th><th>Actions</th></tr></thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td><code style={{ background: 'var(--awp-surface-2)', padding: '2px 8px', borderRadius: 4, color: 'var(--awp-text-strong)' }}>{row.code}</code></td>
                        <td><span className={`status-badge ${row.status === 'Available' ? 'available' : 'used'}`}>{row.status}</span></td>
                        <td>{row.assignedTo || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.usedBy || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.trxId || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.createdAt}</td>
                        <td>{row.expiresAt || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td><button className="admin-btn admin-btn--reject" onClick={() => handleDelete(row.id)} disabled={acting === row.id}>🗑</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination 
                currentPage={page} 
                totalPages={totalPages} 
                onPageChange={setPage} 
              />
            </>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGen && (
        <div className="admin-modal-overlay" onClick={() => setShowGen(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Generate E-Pins</h3>
              <button className="admin-modal-close" onClick={() => setShowGen(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              {genMsg && (
                <div style={{ padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13, background: genMsg.includes('created') ? 'var(--awp-success-soft)' : 'var(--awp-danger-soft)', color: genMsg.includes('created') ? 'var(--awp-success)' : 'var(--awp-danger)' }}>
                  {genMsg}
                </div>
              )}
              <label>Number of Pins (1-100)</label>
              <input type="number" min={1} max={100} value={genCount} onChange={e => setGenCount(e.target.value)} />

              <label style={{ marginTop: 12, display: 'block' }}>Assign to User (optional)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search user by email or username..."
                  value={assignTo}
                  onChange={e => setAssignTo(e.target.value)}
                  onFocus={() => setAssignToFocused(true)}
                  onBlur={() => setTimeout(() => setAssignToFocused(false), 200)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--awp-border)', fontSize: 13, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                  autoComplete="off"
                />
                {assignToFocused && !users.find(u => u.username === assignTo) && (
                  <ul style={{
                    position: 'absolute', top: '100%', left: 0, width: '100%',
                    background: 'var(--awp-surface)', border: '1px solid var(--awp-border)', borderRadius: '4px',
                    maxHeight: '150px', overflowY: 'auto', zIndex: 10, listStyle: 'none', margin: 0, padding: 0,
                    boxShadow: 'var(--awp-shadow-md)'
                  }}>
                    {users.filter(u => u.username.toLowerCase().includes(assignTo.toLowerCase()) || u.email.toLowerCase().includes(assignTo.toLowerCase())).map(u => (
                      <li key={u.id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--awp-divider)', fontSize: 13 }}
                        onMouseDown={() => setAssignTo(u.username)}>
                        {u.username} <span style={{ color: 'var(--awp-text-muted)' }}>({u.email})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p style={{ fontSize: 11, color: 'var(--awp-text-subtle)', marginTop: 6 }}>
                If assigned, the user will see these pins in their "Available E-Pins" page.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--approve" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generating...' : `Generate ${genCount} Pin(s)`}
              </button>
              <button className="admin-btn admin-btn--edit" onClick={() => setShowGen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAvailablePins;
